import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Chip,
  Button,
  IconButton,
  Skeleton,
  Tooltip,
} from '@mui/material';
import { Refresh, Visibility } from '@mui/icons-material';
import CategoryBadges from './CategoryBadges';

interface BookingData {
  id: string;
  bookingRef: string;
  customer: {
    name: string;
    phone?: string;
    email?: string;
  };
  vehicle?: {
    make: string;
    model: string;
    year?: number;
  };
  amount: number;
  status: string;
  serviceType?: string;
  serviceTypeEnhanced?: string;
  serviceCategory?: string;
  createdAt?: string;
  startDate?: string;
  [key: string]: any;
}

interface RecentBookingsTableProps {
  bookings: BookingData[];
  bookingsLoading: boolean;
  activeCategory: string;
  onCategoryChange: (category: string) => void;
  onRefresh: () => void;
}

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
    minimumFractionDigits: 0,
  }).format(amount || 0);
};

const getStatusColor = (
  status: string
): 'warning' | 'success' | 'primary' | 'default' | 'error' => {
  const colors: Record<string, 'warning' | 'success' | 'primary' | 'default' | 'error'> = {
    pending: 'warning',
    confirmed: 'success',
    active: 'primary',
    completed: 'default',
    cancelled: 'error',
  };
  return colors[status] || 'default';
};

const getServiceLabel = (booking: BookingData): string => {
  const serviceType =
    booking.serviceTypeEnhanced || booking.serviceType || booking.serviceCategory || '';
  switch (serviceType) {
    case 'self_drive':
    case 'self-drive':
      return 'Self-Drive';
    case 'with_driver':
    case 'with-driver':
      return 'With Driver';
    case 'chauffeur_driven':
    case 'chauffeur-driven':
      return 'Chauffeur Driven';
    case 'personal_driver':
    case 'driver-hire':
      return 'Driver Hire';
    case 'yacht-charter':
      return 'Yacht Charter';
    case 'hybrid':
      return 'Hybrid';
    default:
      return serviceType
        ? serviceType.replace(/[-_]/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())
        : 'N/A';
  }
};

const formatDate = (dateStr?: string): string => {
  if (!dateStr) return 'N/A';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const RecentBookingsTable: React.FC<RecentBookingsTableProps> = ({
  bookings,
  bookingsLoading,
  activeCategory,
  onCategoryChange,
  onRefresh,
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Reset page when bookings change
  React.useEffect(() => {
    setPage(0);
  }, [bookings, activeCategory]);

  const paginatedBookings = bookings.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const renderSkeletonRows = () =>
    [...Array(5)].map((_, i) => (
      <TableRow key={i}>
        {[...Array(8)].map((__, j) => (
          <TableCell key={j}>
            <Skeleton variant="text" width={j === 7 ? 40 : '80%'} />
          </TableCell>
        ))}
      </TableRow>
    ));

  return (
    <Box>
      {/* Category Badges filter */}
      <CategoryBadges activeCategory={activeCategory} onCategoryChange={onCategoryChange} />

      <Card>
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {activeCategory === 'all'
                ? 'All Bookings'
                : `${activeCategory.charAt(0).toUpperCase()}${activeCategory.slice(1)} Bookings`}
              <Chip
                label={bookings.length}
                color="primary"
                size="small"
                sx={{ ml: 1 }}
              />
            </Typography>
            <Button startIcon={<Refresh />} onClick={onRefresh} size="small">
              Refresh
            </Button>
          </Box>

          {bookingsLoading ? (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Booking Ref</TableCell>
                    <TableCell>Customer</TableCell>
                    <TableCell>Vehicle</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Service Type</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>{renderSkeletonRows()}</TableBody>
              </Table>
            </TableContainer>
          ) : bookings.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h6" color="text.secondary">
                No {activeCategory === 'all' ? '' : activeCategory + ' '}bookings found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {activeCategory === 'all'
                  ? 'When customers make bookings, they will appear here.'
                  : `No ${activeCategory} bookings are currently available.`}
              </Typography>
            </Box>
          ) : (
            <>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow
                      sx={{
                        '& th': {
                          fontWeight: 600,
                          backgroundColor: 'action.hover',
                        },
                      }}
                    >
                      <TableCell>Booking Ref</TableCell>
                      <TableCell>Customer</TableCell>
                      <TableCell>Vehicle</TableCell>
                      <TableCell>Amount</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Service Type</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedBookings.map((booking) => (
                      <TableRow
                        key={booking.id}
                        hover
                        sx={{ cursor: 'pointer', '&:last-child td': { border: 0 } }}
                      >
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {booking.bookingRef}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {booking.customer?.name || 'N/A'}
                          </Typography>
                          {booking.customer?.phone && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {booking.customer.phone}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {booking.vehicle
                            ? `${booking.vehicle.make} ${booking.vehicle.model}${
                                booking.vehicle.year ? ` (${booking.vehicle.year})` : ''
                              }`
                            : 'N/A'}
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, color: 'success.main' }}
                          >
                            {formatCurrency(booking.amount)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={booking.status}
                            size="small"
                            color={getStatusColor(booking.status)}
                            sx={{ textTransform: 'capitalize' }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                            {getServiceLabel(booking)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {formatDate(booking.createdAt || booking.startDate)}
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="View booking">
                            <IconButton size="small">
                              <Visibility fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={bookings.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50]}
              />
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default RecentBookingsTable;
